import { Locales } from '@/utils/translations/types/TypeLanguagesAndLocales';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/nl';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-au';
import 'dayjs/locale/en';
import {
  ChildBirthDateInput,
  DisplayDateFormat,
  TypeDisplayDateFormats,
} from '@/utils/dates/constants';

dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);

// Ensure abbreviations include a trailing period where expected
// These are applied directly without checking current locale
// because dayjs.locale() defaults to 'en'
dayjs.updateLocale('es', {
  monthsShort: [
    'ene.',
    'feb.',
    'mar.',
    'abr.',
    'may.',
    'jun.',
    'jul.',
    'ago.',
    'sept.',
    'oct.',
    'nov.',
    'dic.',
  ],
});

dayjs.updateLocale('nl', {
  monthsShort: [
    'jan.',
    'feb.',
    'mrt.',
    'apr.',
    'mei.',
    'jun.',
    'jul.',
    'aug.',
    'sep.',
    'okt.',
    'nov.',
    'dec.',
  ],
});

export function formatDateForDisplay(
  dateString: string,
  locale: string | undefined,
  format?: TypeDisplayDateFormats,
) {
  const date = dayjs(dateString).toDate();

  if (!_isValidDate(date)) {
    console.warn(
      `[formatDateForDisplay] unable to format provided dateString ${dateString} for locale ${locale}.`,
    );
    return dateString;
  }

  const formatter = _getFormatter(locale, format);
  return formatter.format(date);
}

export function childBirthDateToDateString(
  childBirthDate: ChildBirthDateInput,
) {
  return `${childBirthDate.year}-${childBirthDate.month}-${childBirthDate.day}`;
}

function _isValidDate(date: Date) {
  return !isNaN(date.getTime());
}

function _getFormatter(
  locale: string | undefined,
  formatType?: TypeDisplayDateFormats,
) {
  const validatedLocale = _getValidatedLocale(locale);
  const resolvedLocale = validatedLocale === 'default' ? 'en' : validatedLocale;

  const options: Intl.DateTimeFormatOptions = (() => {
    switch (formatType) {
      case DisplayDateFormat.NumericMonthDayYear:
        return { month: '2-digit', day: '2-digit', year: 'numeric' };
      case DisplayDateFormat.ShortMonthDayYear:
        return { month: 'short', day: 'numeric', year: 'numeric' };
      case DisplayDateFormat.ShortMonthAndDay:
        return { month: 'short', day: 'numeric' };
      default:
        return {};
    }
  })();

  return new Intl.DateTimeFormat(resolvedLocale, options);
}

function _getValidatedLocale(locale: string | undefined) {
  switch (locale) {
    case Locales.UnitedStatesEnglish:
      return Locales.UnitedStatesEnglish;
    case Locales.AustralianEnglish:
      return Locales.AustralianEnglish;
    // note en-EU is not a valid iso language code, formatters should convert to en-150 for accurate localization.
    // @see {https://www.localeplanet.com/icu/en-150/index.html} for more information
    case Locales.EuropeanEnglish:
      return 'en-150';
    case Locales.UnitedKingdomEnglish:
      return Locales.UnitedKingdomEnglish;
    case Locales.StandardDutch:
      return Locales.StandardDutch;
    case Locales.StandardFrench:
      return Locales.StandardFrench;
    case Locales.StandardGerman:
      return Locales.StandardGerman;
    case Locales.StandardSpanish:
      return Locales.StandardSpanish;
    default:
      return 'default';
  }
}

export enum DATE_FORMATS {
  'MMM D, YYYY' = 'MMM D, YYYY',
  'D MMM YYYY' = 'D MMM YYYY',
  'MMM DD' = 'MMM DD',
}

export function formatDate(
  date: string | undefined,
  format: keyof typeof DATE_FORMATS,
  locale: string,
) {
  if (!date) return undefined;

  const resolvedLocale = _getValidatedLocale(locale);
  return dayjs(date).locale(resolvedLocale).format(DATE_FORMATS[format]);
}

export function parseDigiohDob(digiohDobValue: string) {
  const [year, month, day] = digiohDobValue.split('-');
  if (!day || !month || !year)
    return {
      month: '',
      day: '',
      year: '',
    };

  const formattedDay = day.startsWith('0') ? day.slice(1) : day;

  return {
    month,
    day: formattedDay,
    year,
  };
}

export function normalizeAndFormatBirthDateToString(
  date: Partial<{
    year: string | number;
    month: string | number;
    day: string | number;
  }>,
): string {
  const year = String(date.year).padStart(4, '0');
  const month = String(date.month).padStart(2, '0');
  const day = String(date.day).padStart(2, '0');

  return `${year}-${month}-${day}`;
}
